import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import ImportarTarefasIA from '../components/ImportarTarefasIA';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Clock, 
  AlertTriangle, 
  Calendar,
  User,
  ChevronRight,
  CheckCircle2,
  CalendarDays,
  ClipboardList
} from 'lucide-react';
import { formatDate, TASK_TYPE_LABELS, PRIORITY_LABELS, PRIORITY_COLORS, TASK_TYPES_WITH_HALO } from '../lib/utils';
import { toast } from 'sonner';

const Tarefas = () => {
  const { api, can } = useAuth();
  const navigate = useNavigate();
  const [tarefas, setTarefas] = useState([]);
  const [internacoes, setInternacoes] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('todas');

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    fetchTarefas();
  }, []);

  const fetchTarefas = async () => {
    try {
      const [tarefasRes, internacoesRes] = await Promise.all([
        api.get('/tarefas'),
        api.get('/internacoes?ativas=true')
      ]);
      
      setTarefas(tarefasRes.data);
      
      const intMap = {};
      internacoesRes.data.forEach(i => {
        intMap[i.id] = i;
      });
      setInternacoes(intMap);
    } catch (error) {
      console.error('Error fetching tarefas:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter tasks
  const tarefasHoje = tarefas.filter(t => 
    t.data_prevista === today && 
    t.status !== 'concluida' && 
    t.status !== 'cancelada'
  );

  const tarefasVencidas = tarefas.filter(t => 
    t.data_prevista < today && 
    t.status !== 'concluida' && 
    t.status !== 'cancelada'
  );

  const tarefasProximas = tarefas.filter(t => 
    t.data_prevista > today && 
    t.status !== 'concluida' && 
    t.status !== 'cancelada'
  ).sort((a, b) => new Date(a.data_prevista) - new Date(b.data_prevista));

  const tarefasConcluidas = tarefas.filter(t => 
    t.status === 'concluida'
  ).sort((a, b) => new Date(b.concluido_em) - new Date(a.concluido_em));

  // Get current filtered list
  const getFilteredTarefas = () => {
    switch (activeFilter) {
      case 'hoje': return tarefasHoje;
      case 'vencidas': return tarefasVencidas;
      case 'proximas': return tarefasProximas;
      case 'concluidas': return tarefasConcluidas;
      case 'todas': return null; // Will show all sections
      default: return null;
    }
  };

  const handleTarefaClick = (tarefa) => {
    const internacao = internacoes[tarefa.internacao_id];
    if (internacao) {
      navigate(`/pacientes/${internacao.paciente_id}/internacao/${tarefa.internacao_id}`);
    }
  };

  const TarefaCard = ({ tarefa }) => {
    const internacao = internacoes[tarefa.internacao_id];
    const isOverdue = tarefa.data_prevista < today && tarefa.status !== 'concluida' && tarefa.status !== 'cancelada';
    const hasHalo = TASK_TYPES_WITH_HALO.includes(tarefa.tipo);
    
    return (
      <div 
        className={`
          p-4 rounded-lg border cursor-pointer hover:shadow-md transition-all
          ${isOverdue ? 'border-l-4 border-l-red-500 bg-red-50/50 dark:bg-red-900/10' : ''}
          ${tarefa.status === 'concluida' ? 'bg-green-50/50 dark:bg-green-900/10 border-green-200' : ''}
          ${!isOverdue && tarefa.status !== 'concluida' && tarefa.prioridade === 'alta' ? 'border-l-4 border-l-red-500' : ''}
          ${!isOverdue && tarefa.status !== 'concluida' && tarefa.prioridade === 'media' ? 'border-l-4 border-l-yellow-500' : ''}
          ${!isOverdue && tarefa.status !== 'concluida' && tarefa.prioridade === 'baixa' ? 'border-l-4 border-l-blue-500' : ''}
        `}
        onClick={() => handleTarefaClick(tarefa)}
        data-testid={`tarefa-card-${tarefa.id}`}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className={`font-medium ${tarefa.status === 'concluida' ? 'line-through text-muted-foreground' : ''}`}>
                {tarefa.titulo}
              </span>
              
              {/* Priority badge with optional dark purple halo for Imagem/Procedimento */}
              <div className={`relative inline-flex ${hasHalo ? 'p-0.5' : ''}`}>
                {hasHalo && (
                  <div className="absolute inset-0 rounded-full ring-2 ring-purple-800 dark:ring-purple-600" />
                )}
                <Badge className={`${PRIORITY_COLORS[tarefa.prioridade]} ${hasHalo ? 'relative z-10' : ''}`}>
                  {PRIORITY_LABELS[tarefa.prioridade]}
                </Badge>
              </div>
              
              {/* Task type badge */}
              <Badge variant="outline" className={hasHalo ? 'ring-1 ring-purple-800 dark:ring-purple-600' : ''}>
                {TASK_TYPE_LABELS[tarefa.tipo]}
              </Badge>
            </div>
            
            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-600 font-medium' : ''}`}>
                <Calendar className="h-3.5 w-3.5" />
                {formatDate(tarefa.data_prevista)}
              </span>
              
              {internacao && (
                <span className="flex items-center gap-1">
                  <User className="h-3.5 w-3.5" />
                  {internacao.paciente_nome}
                </span>
              )}
              
              {tarefa.responsavel_nome && (
                <span className="text-xs">
                  Resp: {tarefa.responsavel_nome}
                </span>
              )}
            </div>
          </div>
          
          <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
        </div>
      </div>
    );
  };

  const FilterButton = ({ filter, label, count, icon: Icon, activeColor }) => (
    <Button
      variant={activeFilter === filter ? "default" : "outline"}
      className={`
        gap-2 flex-1 sm:flex-none
        ${activeFilter === filter ? activeColor : ''}
      `}
      onClick={() => setActiveFilter(filter)}
      data-testid={`filter-${filter}`}
    >
      <Icon className="h-4 w-4" />
      <span className="hidden sm:inline">{label}</span>
      <Badge 
        variant={activeFilter === filter ? "secondary" : "outline"}
        className="ml-1"
      >
        {count}
      </Badge>
    </Button>
  );

  return (
    <Layout>
      <div className="space-y-6" data-testid="tarefas-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Tarefas</h1>
            <p className="text-muted-foreground mt-1">
              Gerencie as tarefas de todos os pacientes
            </p>
          </div>
          <div className="flex gap-2">
            {can('importar_dados') && can('criar_tarefas') && <ImportarTarefasIA onImportComplete={() => fetchTarefas()} />}
          </div>
        </div>

        {/* Filter Buttons - CLICKABLE */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-2">
              <FilterButton 
                filter="todas" 
                label="Todas" 
                count={tarefas.filter(t => t.status !== 'cancelada').length}
                icon={ClipboardList}
                activeColor="bg-gray-600 hover:bg-gray-700"
              />
              <FilterButton 
                filter="vencidas" 
                label="Vencidas" 
                count={tarefasVencidas.length}
                icon={AlertTriangle}
                activeColor="bg-red-600 hover:bg-red-700"
              />
              <FilterButton 
                filter="hoje" 
                label="Hoje" 
                count={tarefasHoje.length}
                icon={CalendarDays}
                activeColor="bg-blue-600 hover:bg-blue-700"
              />
              <FilterButton 
                filter="proximas" 
                label="Próximas" 
                count={tarefasProximas.length}
                icon={Clock}
                activeColor="bg-yellow-600 hover:bg-yellow-700"
              />
              <FilterButton 
                filter="concluidas" 
                label="Concluídas" 
                count={tarefasConcluidas.length}
                icon={CheckCircle2}
                activeColor="bg-green-600 hover:bg-green-700"
              />
            </div>
          </CardContent>
        </Card>

        {/* Task Sections - Display all in order: Vencidas > Hoje > Próximas > Concluídas */}
        {loading ? (
          <Card>
            <CardContent className="p-6">
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-20 w-full" />)}
              </div>
            </CardContent>
          </Card>
        ) : activeFilter === 'todas' ? (
          <div className="space-y-6">
            {/* Vencidas Section */}
            {tarefasVencidas.length > 0 && (
              <Card className="border-l-4 border-l-red-500">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                    Tarefas Vencidas ({tarefasVencidas.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {tarefasVencidas.map(tarefa => (
                      <TarefaCard key={tarefa.id} tarefa={tarefa} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Hoje Section */}
            <Card className="border-l-4 border-l-blue-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                  <CalendarDays className="h-5 w-5 text-blue-600" />
                  Tarefas de Hoje ({tarefasHoje.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {tarefasHoje.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">Nenhuma tarefa para hoje</p>
                ) : (
                  <div className="space-y-3">
                    {tarefasHoje.map(tarefa => (
                      <TarefaCard key={tarefa.id} tarefa={tarefa} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Próximas Section */}
            {tarefasProximas.length > 0 && (
              <Card className="border-l-4 border-l-yellow-500">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                    <Clock className="h-5 w-5 text-yellow-600" />
                    Próximas Tarefas ({tarefasProximas.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {tarefasProximas.map(tarefa => (
                      <TarefaCard key={tarefa.id} tarefa={tarefa} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Concluídas Section */}
            {tarefasConcluidas.length > 0 && (
              <Card className="border-l-4 border-l-green-500">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    Tarefas Concluídas ({tarefasConcluidas.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {tarefasConcluidas.map(tarefa => (
                      <TarefaCard key={tarefa.id} tarefa={tarefa} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          /* Single Section View */
          <Card className={`
            ${activeFilter === 'hoje' ? 'border-l-4 border-l-blue-500' : ''}
            ${activeFilter === 'vencidas' ? 'border-l-4 border-l-red-500' : ''}
            ${activeFilter === 'proximas' ? 'border-l-4 border-l-yellow-500' : ''}
            ${activeFilter === 'concluidas' ? 'border-l-4 border-l-green-500' : ''}
          `}>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                {activeFilter === 'hoje' && <CalendarDays className="h-5 w-5 text-blue-600" />}
                {activeFilter === 'vencidas' && <AlertTriangle className="h-5 w-5 text-red-600" />}
                {activeFilter === 'proximas' && <Clock className="h-5 w-5 text-yellow-600" />}
                {activeFilter === 'concluidas' && <CheckCircle2 className="h-5 w-5 text-green-600" />}
                
                {activeFilter === 'hoje' && `Tarefas do Dia (${tarefasHoje.length})`}
                {activeFilter === 'vencidas' && `Tarefas Vencidas (${tarefasVencidas.length})`}
                {activeFilter === 'proximas' && `Próximas Tarefas (${tarefasProximas.length})`}
                {activeFilter === 'concluidas' && `Tarefas Concluídas (${tarefasConcluidas.length})`}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {getFilteredTarefas()?.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <ClipboardList className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="text-lg">
                    {activeFilter === 'hoje' && 'Nenhuma tarefa para hoje!'}
                    {activeFilter === 'vencidas' && 'Nenhuma tarefa vencida!'}
                    {activeFilter === 'proximas' && 'Nenhuma tarefa futura agendada'}
                    {activeFilter === 'concluidas' && 'Nenhuma tarefa concluída'}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {getFilteredTarefas()?.map(tarefa => (
                    <TarefaCard key={tarefa.id} tarefa={tarefa} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default Tarefas;
